import type { PlainDate } from "@sa-nu/shared-utils/date";
import { parsePlainDate } from "@sa-nu/shared-utils/date";
import type { ComponentProps } from "react";
import React, { useEffect, useRef, useState } from "react";

import type { DocumentType, FragmentType } from "../../../__generated__/gql";
import { graphql, useFragment } from "../../../__generated__/gql";
import type { AccommodationReservationProvider } from "../../../__generated__/gql/graphql";
import { useGtm } from "../../../lib/google-tag-manager/gtm";
import { useInNativeApp } from "../../_hooks/use-in-native-app";
import { waitForRef } from "../../utils/wait-for-ref";

import DraftReservation from "./draft-reservation";
import type { SelectedAccommodationReservation } from "./reservation-calendar";
import { ReservationCalendar } from "./reservation-calendar";

export const OriginalReservationFragment = graphql(`
  fragment OriginalReservationOnReservationForm on AccommodationReservation {
    firstNight
    lastNight
    mutationScope
    accommodation {
      id
      facilityTypes
    }
    ...OriginalAccommodationReservationOnReservationCalendar
    ...OriginalAccommodationReservationOnDraftReservation
  }
`);

const UserInfoFragment = graphql(`
  fragment UserReservationCountForLog on User {
    stayedReservationCount
  }
`);

export function useReservationSelection({
  originalReservation,
}: {
  originalReservation: DocumentType<typeof OriginalReservationFragment> | null;
}) {
  const [selected, setSelected] =
    useState<SelectedAccommodationReservation | null>(null);

  useEffect(() => {
    if (!originalReservation) return;
    if (selected !== null) return;
    setSelected({
      firstNight: parsePlainDate(originalReservation.firstNight),
      lastNight: parsePlainDate(originalReservation.lastNight),
      accommodationId: originalReservation.accommodation.id,
      accommodationFacilities: originalReservation.accommodation.facilityTypes,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [originalReservation]);

  return { selected, setSelected };
}

/**
 * 予約画面のベース
 * 予約カレンダー{@link ReservationCalendar}と、予約フォーム{@link DraftReservation}をまとめる役割をする
 */
export function ReservationFormBase({
  provider,
  originalReservationData,
  userInfo,
  minNight,
  maxNight,
  initialDate,
  draftReservationProps = {},
}: {
  provider: AccommodationReservationProvider;
  originalReservationData: FragmentType<
    typeof OriginalReservationFragment
  > | null;
  userInfo: FragmentType<typeof UserInfoFragment> | null;
  minNight: PlainDate;
  maxNight: PlainDate;
  initialDate?: PlainDate;
  draftReservationProps?: Pick<
    ComponentProps<typeof DraftReservation>,
    "cancelFeePolicyHidden" | "additionalNotice" | "termsAgreementNoticeHidden"
  >;
}) {
  const originalReservation = useFragment(
    OriginalReservationFragment,
    originalReservationData,
  );
  const reservationCount = useFragment(
    UserInfoFragment,
    userInfo,
  )?.stayedReservationCount;

  const { selected, setSelected } = useReservationSelection({
    originalReservation,
  });

  const formRef = useRef<HTMLFormElement>(null);

  function handleSelectedInReservationCalendar(
    value: SelectedAccommodationReservation,
  ) {
    setSelected(value);
    waitForRef(formRef, (el) => {
      el.scrollIntoView({ behavior: "smooth" });
    });
  }

  const isInNativeApp = useInNativeApp();
  const isChangeReesrvation = originalReservation != null;
  const gtm = useGtm();
  useEffect(() => {
    // 初期表示だとisInNativeAppがundefinedなので、確定してから送る
    if (isInNativeApp !== undefined) {
      gtm.sendReservationFormViewedEvent({
        provider,
        pastReservationCount: reservationCount,
        mode: isChangeReesrvation ? "change" : "new",
        platform: isInNativeApp ? "app" : "web",
      });
    }
  }, [gtm, provider, reservationCount, isChangeReesrvation, isInNativeApp]);

  return (
    <>
      <ReservationCalendar
        providerOrLimit={provider}
        selected={selected}
        setSelected={handleSelectedInReservationCalendar}
        originalReservationData={originalReservation}
        minNight={minNight}
        maxNight={maxNight}
        initialDate={initialDate}
      />
      {selected && (
        <DraftReservation
          key={selected.accommodationId}
          provider={provider}
          selected={selected}
          originalReservationData={originalReservation}
          formRef={formRef}
          {...draftReservationProps}
        />
      )}
    </>
  );
}
